// ─────────────────────────────────────────────
// API Client
// ─────────────────────────────────────────────

// Use environment variable if set, otherwise use current origin (same domain)
// This works for both development (localhost) and production (same domain)
const API_BASE_URL = import.meta.env.VITE_API_URL || 
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000');

export interface ApiError {
  error: string;
  message: string;
  details?: unknown;
}

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> | undefined),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        error: 'Unknown Error',
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));

      // Handle 401 Unauthorized
      if (response.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        // Redirect to login
        if (window.location.pathname !== '/admin/login') {
          window.location.href = '/admin/login';
        }
      }

      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const api = new ApiClient();

// Auth API methods
export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ accessToken: string; refreshToken: string; user: unknown }>(
      '/auth/login',
      { email, password }
    ),
  register: (email: string, password: string, fullName: string) =>
    api.post<{ accessToken: string; refreshToken: string; user: unknown }>(
      '/auth/register',
      { email, password, fullName }
    ),
  refresh: (refreshToken: string) =>
    api.post<{ accessToken: string }>('/auth/refresh', { refreshToken }),
  me: () => api.get<{ user: unknown; organizations: unknown[] }>('/admin/me'),
};

// Admin API methods
export const adminApi = {
  getOrganizations: () => api.get('/admin/organizations'),
  getUsers: () => api.get('/admin/users'),
  getOrganizationStats: (organizationId: string) =>
    api.get(`/admin/organizations/${organizationId}/stats`),
  getEmailAccounts: (organizationId: string) =>
    api.get(`/admin/organizations/${organizationId}/email-accounts`),
  getEmailAccountStats: (organizationId: string) =>
    api.get(`/admin/organizations/${organizationId}/email-accounts/stats`),
  createEmailAccount: (organizationId: string, data: unknown) =>
    api.post(`/admin/organizations/${organizationId}/email-accounts`, data),
  updateEmailAccount: (
    organizationId: string,
    emailAccountId: string,
    data: unknown
  ) =>
    api.put(
      `/admin/organizations/${organizationId}/email-accounts/${emailAccountId}`,
      data
    ),
  deleteEmailAccount: (organizationId: string, emailAccountId: string) =>
    api.delete(
      `/admin/organizations/${organizationId}/email-accounts/${emailAccountId}`
    ),
  getEmails: (
    organizationId: string,
    emailAccountId: string,
    params?: { folder?: string; limit?: number; offset?: number; unreadOnly?: boolean }
  ) => {
    const query = new URLSearchParams();
    if (params?.folder) query.set('folder', params.folder);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    if (params?.unreadOnly) query.set('unreadOnly', 'true');
    return api.get(
      `/admin/organizations/${organizationId}/email-accounts/${emailAccountId}/emails?${query.toString()}`
    );
  },
  syncEmails: (organizationId: string, emailAccountId: string, password: string) =>
    api.post(
      `/admin/organizations/${organizationId}/email-accounts/${emailAccountId}/emails/sync`,
      { password }
    ),
  getEmail: (
    organizationId: string,
    emailAccountId: string,
    emailId: string
  ) =>
    api.get(
      `/admin/organizations/${organizationId}/email-accounts/${emailAccountId}/emails/${emailId}`
    ),
  markEmailRead: (
    organizationId: string,
    emailAccountId: string,
    emailId: string,
    isRead: boolean
  ) =>
    api.patch(
      `/admin/organizations/${organizationId}/email-accounts/${emailAccountId}/emails/${emailId}/read`,
      { isRead }
    ),
  sendTestEmail: (
    organizationId: string,
    emailAccountId: string,
    to: string
  ) =>
    api.post(
      `/admin/organizations/${organizationId}/email-accounts/${emailAccountId}/send-test`,
      { to }
    ),
};

